import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { heroParallax, prefersReducedMotion, EASE, softFloat } from "../utils/motion";
import { heroConfig } from "../data/portfolioData";

gsap.registerPlugin(ScrollTrigger);

const Hero = ({ onOpenModal }) => {
  const heroRef    = useRef(null);
  const glowRef    = useRef(null);
  const glowRef2   = useRef(null);
  const featuredRef = useRef(null);

  // Dual ambient glow — counter-moving orbs for depth
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const glow  = glowRef.current;
    const glow2 = glowRef2.current;
    if (!glow || !glow2) return;

    const tl1 = gsap.timeline({ repeat: -1, yoyo: true });
    tl1.to(glow, { x: 60, y: -28, scale: 1.14, duration: 10, ease: "sine.inOut" });
    tl1.to(glow, { x: -35, y: 32, scale: 0.92, duration:  9, ease: "sine.inOut" });

    const tl2 = gsap.timeline({ repeat: -1, yoyo: true, delay: 4.5 });
    tl2.to(glow2, { x: -45, y: 22, scale: 1.10, duration: 12, ease: "sine.inOut" });
    tl2.to(glow2, { x:  28, y:-20, scale: 0.90, duration: 11, ease: "sine.inOut" });

    return () => { tl1.kill(); tl2.kill(); };
  }, []);

  // Entrance animation — cinematic staggered reveal
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.from(".hero-status", {
          opacity: 0, x: -16, duration: 0.40,
        })
        .from(".hero-eyebrow", {
          opacity: 0, x: -24, duration: 0.55,
        }, "-=0.15")
        .from(".hero-title", {
          opacity: 0, y: 40, scale: 0.97, duration: 0.88, ease: "power4.out",
        }, "-=0.28")
        .from(".hero-subtitle", {
          opacity: 0, y: 18, duration: 0.52,
        }, "-=0.40")
        .from(".hero-ctas", {
          opacity: 0, y: 16, scale: 0.97, duration: 0.48,
        }, "-=0.28")
        .from(".hero-badges > *", {
          opacity: 0, y: 10, duration: 0.36, stagger: 0.065,
        }, "-=0.22")
        .from(".hero-scroll-hint", {
          opacity: 0, y: -8, duration: 0.38,
        }, "-=0.10")
        .from(".hero-featured", {
          opacity: 0, x: 42, duration: 0.80, ease: "power4.out",
        }, "-=0.72")
        .from(".featured-metric", {
          opacity: 0, y: 16, scale: 0.78, duration: 0.48, stagger: 0.10, ease: EASE.spring,
        }, "-=0.40");
    }, hero);

    return () => ctx.revert();
  }, []);

  // Counter animation — numeric featured-metric-value spans count up on mount
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const hero = heroRef.current;
    if (!hero) return;

    const ctx = gsap.context(() => {
      hero.querySelectorAll(".featured-metric-value").forEach((el) => {
        const raw = el.textContent.trim();
        const m = raw.match(/^(\d+(?:\.\d+)?)(.*?)$/);
        if (!m) return; // skip non-numeric like "AI"
        const to       = parseFloat(m[1]);
        const suffix   = m[2] || "";
        const decimals = (m[1].split(".")[1] || "").length;

        // Use a delayed plain tween (no ScrollTrigger needed — hero is always
        // in view on load). Store final value then animate once mounted.
        const obj = { val: 0 };
        gsap.to(obj, {
          val: to,
          duration: 1.6,
          delay: 1.3,
          ease: "power2.out",
          immediateRender: false, // don't touch DOM until tween actually starts
          onUpdate() {
            el.textContent =
              (decimals > 0 ? obj.val.toFixed(decimals) : Math.round(obj.val)) +
              suffix;
          },
          onComplete() {
            el.textContent =
              (decimals > 0 ? to.toFixed(decimals) : to) + suffix;
          },
        });
      });
    }, hero);

    return () => ctx.revert();
  }, []);

  // Soft float + scroll parallax on featured panel
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const el = featuredRef.current;
    if (!el) return;
    const floatTw  = softFloat(el, { yMove: 8, duration: 5.5 });
    const scrollTw = heroParallax(el, -12);
    return () => {
      floatTw?.kill();
      scrollTw?.scrollTrigger?.kill();
      scrollTw?.kill();
    };
  }, []);

  const scrollToProjects = () => {
    const el = document.getElementById("projects");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero" id="hero" ref={heroRef}>
      <div className="hero-bg-glow"  ref={glowRef}  aria-hidden="true" />
      <div className="hero-bg-glow-2" ref={glowRef2} aria-hidden="true" />
      <div className="hero-bg-grain" aria-hidden="true" />

      <div className="hero-inner">
        {/* Left: Content */}
        <div className="hero-content">
          {/* Top meta row: status pill + role eyebrow on one line */}
          <div className="hero-meta-row">
            <div className="hero-status">
              <span className="hero-status-dot" aria-hidden="true" />
              <span>{heroConfig.availableForProjects ? "Available for Projects" : "Currently Unavailable"}</span>
            </div>
            <span className="hero-eyebrow">{heroConfig.role}</span>
          </div>
          <h1 className="hero-title">
            Morpheus<br />
            <span className="hero-title-accent">Vibaen J</span>
          </h1>
          <p className="hero-subtitle">
            I design and build scalable, production-ready software - from concept through cloud deployment.
          </p>

          <div className="hero-content-divider" aria-hidden="true" />

          <div className="hero-ctas">
            <button
              className="cta-button cta-primary"
              onClick={() => onOpenModal("tele-consultation")}
            >
              View Featured Project <span className="cta-arrow">→</span>
            </button>
            <button
              className="cta-button cta-secondary"
              onClick={scrollToProjects}
            >
              Explore All Work
            </button>
          </div>

          <div className="hero-badges">
            <span className="hero-badges-label">Stack</span>
            <span className="hero-badge">React</span>
            <span className="hero-badge">Node.js</span>
            <span className="hero-badge">AWS</span>
            <span className="hero-badge">Python</span>
            <span className="hero-badge">AI / ML</span>
            <span className="hero-badge">Full-Stack</span>
          </div>

          {/* Scroll hint */}
          <div className="hero-scroll-hint" aria-hidden="true">
            <span className="scroll-hint-line" />
            <span className="scroll-hint-label">Scroll</span>
          </div>
        </div>

        {/* Right: Featured flagship showcase */}
        <div className="hero-featured" aria-hidden="true" ref={featuredRef}>
          <div className="featured-showcase">
            <div className="featured-showcase-glow" aria-hidden="true" />
            <div className="featured-card">
              <div className="featured-accent" />
              {/* Project screenshot */}
              <div className="featured-img">
                <img src="/project-images/app.jpg" alt="" aria-hidden="true" />
                <div className="featured-img-overlay" aria-hidden="true" />
                <div className="featured-img-badge" aria-hidden="true">
                  Case Study
                </div>
              </div>
              <div className="featured-content">
                <span className="featured-label">
                  <span className="featured-label-dot" aria-hidden="true" />
                  Featured Project
                </span>
                <h3 className="featured-title">Tele-Consultation Platform</h3>
                <p className="featured-desc">
                  A full-stack healthcare platform with real-time video, Stripe payments, and AWS serverless architecture.
                </p>
                <div className="featured-tech">
                  <span>React Native</span>
                  <span>Node.js</span>
                  <span>AWS</span>
                  <span>Stripe</span>
                </div>
              </div>
              <div className="featured-metrics">
                <div className="featured-metric">
                  <span className="featured-metric-value">3+</span>
                  <span className="featured-metric-label">Years Exp</span>
                </div>
                <div className="featured-metric-divider" aria-hidden="true" />
                <div className="featured-metric">
                  <span className="featured-metric-value">10+</span>
                  <span className="featured-metric-label">Projects</span>
                </div>
                <div className="featured-metric-divider" aria-hidden="true" />
                <div className="featured-metric">
                  <span className="featured-metric-value">2</span>
                  <span className="featured-metric-label">Internships</span>
                </div>
                <div className="featured-metric-divider" aria-hidden="true" />
                <div className="featured-metric">
                  <span className="featured-metric-value">AI</span>
                  <span className="featured-metric-label">Integrated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lower proof strip — premium editorial credibility row */}
      <div className="hero-proof-strip" aria-hidden="true">
        <span className="proof-item">Full-Stack Development</span>
        <span className="proof-sep" aria-hidden="true" />
        <span className="proof-item">AI &amp; Machine Learning</span>
        <span className="proof-sep" aria-hidden="true" />
        <span className="proof-item">Cloud Architecture</span>
        <span className="proof-sep" aria-hidden="true" />
        <span className="proof-item">10+ Production Projects</span>
        <span className="proof-sep" aria-hidden="true" />
        <span className="proof-item">3+ Years Experience</span>
      </div>
    </section>
  );
};

export default Hero;
